import {
    Component,
    DestroyRef,
    ElementRef,
    OnDestroy,
    OnInit,
    computed,
    effect,
    forwardRef,
    inject,
    input,
    model,
    output,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { of } from 'rxjs';
import { debounceTime, filter, map, switchMap } from 'rxjs/operators';

export interface AutocompleteConfig {
    searchable?: boolean;
    multiple?: boolean;
    placeholder?: string;
    label?: string;
}

/** TOBEDELETED | TOBEREMOVED
 *
 * Temporary autocomplete only while the component pantry is broken for this particular component.
 *
 * Generated using ANTIGRAVITY
 */
@Component({
    selector: 'app-autocomplete',
    standalone: true,
    imports: [ReactiveFormsModule],
    templateUrl: './autocomplete.component.html',
    styleUrl: './autocomplete.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteComponent),
            multi: true,
        },
    ],
})
export class AutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy {
    /** Reference to the component's host DOM element */
    private readonly elementRef = inject(ElementRef);

    /** Tears down search and other subscriptions when the component is destroyed */
    private readonly destroyRef = inject(DestroyRef);

    /** Optional input to control how much characters are allowed to trigger a network requests. Defaults to 1 */
    public readonly minCharsForSearch = input<number>(1);

    /**
     * Component Inputs (Signal-based)
     */
    options = input<any[]>([]);
    config = input<AutocompleteConfig, AutocompleteConfig>(
        {
            searchable: true,
            multiple: false,
            placeholder: 'Select an option',
            label: '',
        },
        {
            transform: (value) => ({ ...this._defaultConfig, ...value }),
        },
    );
    loading = input<boolean>(false);
    displayWith = input<string>('label');
    valueWith = input<string>('value');
    /**
     * When set (e.g. `selectionKey`), row equality for highlighting and multi-select uses this option property
     * instead of {@link valueWith}. Emitted form value is still {@link getOptionValue}.
     */
    selectionIdentityProp = input<string | null>(null);

    error = input<string>('');
    width = input<string>('100%');
    size = input<'sm' | 'md' | 'lg' | 'xl'>('md');

    /**
     * Component Outputs (Signal-based)
     */
    onSelect = output<any>();
    loadingMore = input<boolean>(false);

    onSearch = output<string>();
    onFocus = output<void>();
    onScrollEnd = output<void>();

    /**
     * Internal State
     */
    private _defaultConfig: AutocompleteConfig = {
        searchable: true,
        multiple: false,
        placeholder: 'Select an option',
        label: '',
    };

    /** Whether the options dropdown panel is visible */
    protected readonly isOpen = signal(false);

    /** Window click listener used to close the panel on outside interaction */
    private outsideClickListener: ((event: Event) => void) | null = null;

    /**
     * Selected option rows; kept as an array internally for single- and multi-select.
     * Updated from the parent via {@link writeValue} and from user interaction in the template.
     */
    public readonly selectedOptions = model<{ label: string; value: string }[]>([]);

    /** Search input bound while the dropdown is open and {@link AutocompleteConfig.searchable} is true */
    protected readonly searchControl = new FormControl('');

    /**
     * Holds the last typed search value.
     * Preserves the input search value when {@link AutocompleteConfig.searchable} is true
     * and when dropdown is opened again
     */
    protected preservedSearchValue = signal<string>('');

    /** Trigger label when closed: placeholder, multi-select summary, or the selected option label */
    protected readonly dropdownLabel = computed(() => {
        const selected = this.selectedOptions();
        const config = this.config();

        if (selected.length === 0) return config.placeholder;

        if (config.multiple) {
            return `Selected (${selected.length}) ${config.label || 'items'}`;
        }

        return this.getOptionLabel(selected[0]);
    });

    constructor() {
        /**
         * Preserves the search input value when user closes the autocomplete
         * and the search input had value. Preserves when user opens the search input (autocomplete again)
         */
        effect(() => {
            const open = this.isOpen();
            const preservedSearchValue = untracked(() => this.preservedSearchValue());

            if (open && preservedSearchValue) {
                this.searchControl.setValue(preservedSearchValue);
            }
        });
    }

    /**
     * @public
     * @description Wires debounced search, immediate empty-search emit, single-select clear-on-empty, and outside-click close
     * @returns void
     */
    public ngOnInit(): void {
        this.searchControl.valueChanges
            .pipe(
                map((raw) => raw ?? ''),
                filter((v) => v.trim() === '' || v.trim().length >= this.minCharsForSearch()),
                switchMap((raw) => {
                    const v = raw ?? '';
                    if (v.trim() === '') {
                        return of(v);
                    }
                    return of(v).pipe(debounceTime(300));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((searchValue) => {
                this.handleSearchValueChange(searchValue ?? '');
                this.preservedSearchValue.set(searchValue);
            });

        // Use capture phase for the outside click listener to ensure it works
        // even if propagation is stopped by a parent component (like a modal content area)
        if (typeof window !== 'undefined') {
            this.outsideClickListener = (event: Event) => {
                const target = event.target as HTMLElement;
                if (this.isOpen() && !this.elementRef.nativeElement.contains(target)) {
                    this.closeDropdownSurface();
                }
            };
            window.addEventListener('click', this.outsideClickListener, { capture: true });
        }
    }

    /**
     * @public
     * @description Removes the document-level outside-click listener
     * @returns void
     */
    public ngOnDestroy(): void {
        if (this.outsideClickListener && typeof window !== 'undefined') {
            window.removeEventListener('click', this.outsideClickListener, { capture: true });
        }
    }

    /** Disabled state driven by {@link setDisabledState} from the parent form control */
    protected readonly isDisabled = signal(false);

    /** ControlValueAccessor callback registered by Angular forms */
    private onChange: (value: unknown) => void = () => {};

    /** ControlValueAccessor touched callback registered by Angular forms */
    private onTouched: () => void = () => {};

    /** Holds the debounced timer reference to delay emitting the search value */
    private searchTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * @public
     * @description Syncs the form control value into {@link selectedOptions}, resolving primitives against {@link options}
     * @param obj - Value from the parent `FormControl` (primitive, object, or array in multi mode)
     * @returns void
     */
    public writeValue(obj: unknown): void {
        const findOption = (val: any) => {
            if (val && (typeof val === 'string' || typeof val === 'number')) {
                const opts = this.options();
                const byValue = opts.find((opt) => this.getOptionValue(opt) === val);
                if (byValue) return byValue;

                const idProp = this.selectionIdentityProp();
                if (idProp && typeof val === 'string') {
                    const byIdentity = opts.find((opt) => opt?.[idProp] === val);
                    if (byIdentity) return byIdentity;
                }

                // Legacy drafts: city control held plain city name; disambiguate against duplicate names.
                if (typeof val === 'string' && !val.includes('|')) {
                    const byCity = opts.find((opt) => opt?.city === val);
                    if (byCity) return byCity;
                }

                return val;
            }
            return val;
        };

        if (this.config().multiple) {
            const values = Array.isArray(obj) ? obj : obj ? [obj] : [];
            this.selectedOptions.set(values.map((v) => findOption(v)));
        } else {
            this.selectedOptions.set(obj ? [findOption(obj)] : []);
        }
    }

    /**
     * @public
     * @description Registers the Angular forms change callback
     * @param fn - Callback invoked when the control value changes
     * @returns void
     */
    public registerOnChange(fn: (value: unknown) => void): void {
        this.onChange = fn;
    }

    /**
     * @public
     * @description Registers the Angular forms touched callback
     * @param fn - Callback invoked when the control is marked touched
     * @returns void
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * @public
     * @description Applies the disabled state from the parent form control
     * @param isDisabled - Whether interaction should be blocked
     * @returns void
     */
    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    protected onListScroll(event: Event): void {
        const el = event.target as HTMLElement;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
            this.onScrollEnd.emit();
        }
    }

    /**
     * @private
     * @description Applies search text changes: emits trimmed query (empty immediately, typed debounced) and clears single-select when the user deletes all text while open
     * Debounces the search value by 500ms
     * @param searchValue - Raw value from {@link searchControl}
     * @returns void
     */
    private handleSearchValueChange(searchValue: string): void {
        const trimmed = searchValue.trim();

        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }

        if (trimmed === '') {
            this.onSearch.emit(trimmed);
        } else {
            this.searchTimer = setTimeout(() => {
                this.onSearch.emit(trimmed);
            }, 500);
        }

        if (
            trimmed === '' &&
            !this.config().multiple &&
            this.selectedOptions().length > 0 &&
            this.isOpen()
        ) {
            this.clearSingleSelectionFromSearch();
        }
    }

    /**
     * @private
     * @description Clears single-select value when the user empties the prefilled search field
     * @returns void
     */
    private clearSingleSelectionFromSearch(): void {
        this.selectedOptions.set([]);
        this.onChange('');
        this.onSelect.emit(null);
        this.onTouched();
        this.preservedSearchValue.set('');
    }

    /**
     * @private
     * @description Focuses the search input after the dropdown opens
     * @returns void
     */
    private focusSearch(): void {
        setTimeout(() => {
            const inputElement = this.elementRef.nativeElement.querySelector(
                '.ntv-autocomplete__search-input',
            );
            if (inputElement) {
                (inputElement as HTMLElement).focus();
            }
        }, 100);
    }

    /**
     * @private
     * @description Closes the dropdown and resets the search field without changing the stored selection
     * @returns void
     */
    private closeDropdownSurface(): void {
        if (!this.isOpen()) {
            return;
        }
        this.isOpen.set(false);
        this.onTouched();
        if (this.config().searchable) {
            this.searchControl.setValue('', { emitEvent: false });
        }
    }

    /**
     * @protected
     * @description Opens or closes the dropdown; prefills search with the current label when opening single-select searchable mode
     * @param event - Optional click event to stop propagation
     * @returns void
     */
    protected toggleDropdown(event?: Event): void {
        if (this.isDisabled()) return;

        if (event) {
            event.stopPropagation();
        }

        if (this.isOpen()) {
            this.closeDropdownSurface();
            return;
        }

        this.isOpen.set(true);
        const cfg = this.config();
        if (cfg.searchable) {
            if (!cfg.multiple && this.selectedOptions().length > 0) {
                this.searchControl.setValue(this.getOptionLabel(this.selectedOptions()[0]), {
                    emitEvent: false,
                });
            } else {
                this.searchControl.setValue('', { emitEvent: false });
            }
            this.focusSearch();
        }
    }

    /**
     * Emits focus event to the parent component
     */
    protected onTriggerFocus(): void {
        this.onFocus.emit();
    }

    /**
     * @protected
     * @description Selects an option, closes single-select mode, and clears the transient search buffer
     * @param option - Option row from the dropdown list
     * @param event - Click event used to stop propagation
     * @returns void
     */
    protected selectOption(option: any, event: Event): void {
        if (this.isDisabled()) return;

        event.stopPropagation();
        const config = this.config();

        if (config.multiple) {
            this.toggleSelection(option);
        } else {
            this.selectedOptions.set([option]);
            this.isOpen.set(false);
            if (config.searchable) {
                this.searchControl.setValue('', { emitEvent: false });
            }
            this.onSelect.emit(option);
            this.onChange(option.value);
            this.onTouched();
        }
    }

    /**
     * @private
     * @description Adds or removes an option from the selection in multi-select mode
     * @param option - Option row toggled in the dropdown
     * @returns void
     */
    private toggleSelection(option: any): void {
        const currentSelected = this.selectedOptions();
        const index = currentSelected.findIndex((item) =>
            this.selectionIdentityEquals(item, option),
        );

        let newValue: any[];
        if (index > -1) {
            newValue = [...currentSelected];
            newValue.splice(index, 1);
        } else {
            newValue = [...currentSelected, option];
        }

        this.selectedOptions.set(newValue);
        this.onSelect.emit(newValue);
        this.onChange(newValue);
        this.onTouched();
    }

    /**
     * Check if option is selected
     */
    protected isSelected(option: any): boolean {
        return this.selectedOptions().some((item) => this.selectionIdentityEquals(item, option));
    }

    /**
     * @protected
     * @description Clears the current selection via the trigger clear control and emits form and parent events
     * @param event - Click event used to stop propagation
     * @returns void
     */
    protected clearSelection(event: Event): void {
        if (this.isDisabled()) return;

        event.stopPropagation();
        this.selectedOptions.set([]);
        const emitValue = this.config().multiple ? [] : '';
        this.onChange(emitValue);
        this.onSelect.emit(this.config().multiple ? emitValue : null);
        this.onTouched();
        this.searchControl.setValue('');
    }

    /**
     * Get icon for an option
     */
    protected getIcon(option: any): string {
        if (!option) return '';
        if (typeof option === 'string') return option;
        return option['iconKey'] || '';
    }

    /**
     * Get icon size for an option
     *
     * If nothing found, defaults to 10
     */
    protected getIconSize(option: any): string {
        if (!option) return '';
        if (typeof option === 'string') return '10';
        return option['iconSize'] || '10';
    }

    /**
     * @private
     * @description Selection row key: primitives as-is; objects use {@link selectionIdentityProp} when set, else {@link getOptionValue}; null if absent.
     * @param option - Option object, string, number, or other value from {@link options}
     * @returns Primitive or reference used for strict-equality selection matching, or null when option is absent
     */
    private getSelectionIdentity(option: any): any {
        if (!option) return null;
        if (typeof option === 'string' || typeof option === 'number') return option;
        const prop = this.selectionIdentityProp();
        if (prop && option[prop] != null && option[prop] !== '') {
            return option[prop];
        }
        return this.getOptionValue(option);
    }

    /**
     * @private
     * @description True when {@link getSelectionIdentity} matches for both arguments (highlight / multi-select row equality).
     * @param a - First option or primitive from the dropdown list or current selection
     * @param b - Second option or primitive to compare
     * @returns True when both arguments resolve to the same selection identity
     */
    private selectionIdentityEquals(a: any, b: any): boolean {
        return this.getSelectionIdentity(a) === this.getSelectionIdentity(b);
    }

    /**
     * Get label for an option
     */
    protected getOptionLabel(option: any): string {
        if (!option) return '';
        if (typeof option === 'string') {
            return this.formatPrimitiveOptionLabel(option);
        }
        return option[this.displayWith()] || '';
    }

    /**
     * City rows use pipe-delimited `selectionKey`; when Angular syncs before options arrive,
     * {@link selectedOptions} briefly holds that string — show City, State like option rows do.
     */
    private formatPrimitiveOptionLabel(value: string): string {
        if (this.selectionIdentityProp() !== 'selectionKey' || !value.includes('|')) {
            return value;
        }
        const parts = value
            .split('|')
            .map((p) => p.trim())
            .filter((p) => p.length > 0);
        if (parts.length < 2) {
            return value;
        }
        return `${parts[0]}, ${parts[1]}`;
    }

    /**
     * Get value for an option
     */
    protected getOptionValue(option: any): any {
        if (!option) return null;
        if (typeof option === 'string') return option;
        return option[this.valueWith()] || option;
    }
}
